import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, ChatCircleDots, Buildings, ShieldStar, ArrowRight, Plus, Broom, X, CheckSquareOffset } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { AnnouncementDetail, AnnouncementsAllSheet } from "../components/AnnouncementModals";

export default function Home() {
  const { user, isFeatureEnabled } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/api/announcements").then(({ data }) => setItems(data.items)).catch(() => {});
    api.get("/api/complaints").then(({ data }) => setComplaints(data.items.slice(0, 3))).catch(() => {});
  }, []);

  const hour = new Date().getHours();
  const greet = hour < 12 ? t("home.morning") : hour < 17 ? t("home.afternoon") : t("home.evening");
  const visibleAnn = useMemo(() => items.slice(0, 3), [items]);

  return (
    <div className="space-y-7 font-sans" data-testid="home-screen">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">{greet}</p>
          <h1 className="font-display text-3xl font-extrabold leading-tight text-white">
            {user?.name?.split(" ")[0] || "Resident"} <span className="text-neon-cyan">·</span>
          </h1>
          {user?.flat && (
            <p className="mt-1 text-sm text-white/60">{t("home.flat")} <span className="font-mono font-semibold text-white">{user.flat.label}</span> · {t("home.society")}</p>
          )}
        </div>
        <button onClick={() => navigate("/account")} data-testid="home-avatar"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/[0.05] font-display text-base font-bold text-white">
          {user?.name?.[0]?.toUpperCase() || "?"}
        </button>
      </div>

      {/* Society pulse */}
      <div className="card overflow-hidden p-0">
        <div className="relative p-5">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-neon-cyan/20 blur-3xl" />
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-neon-cyan">
            <ShieldStar size={13} weight="fill" /> {t("home.pulse")}
          </p>
          <p className="mt-2 font-display text-2xl font-semibold leading-snug text-white">
            {t("home.clear")} <span className="text-white/55">{t("home.noUrgent")}</span>
          </p>
          <div className="mt-4 flex gap-3 text-[12px]">
            <div className="chip border-neon-lime/30 text-neon-lime">{complaints.filter((c) => c.status === "OPEN").length} {t("home.openCount")}</div>
            <div className="chip">{complaints.filter((c) => c.status === "IN_PROGRESS").length} {t("home.inProgressCount")}</div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      {(isFeatureEnabled("complaints") || isFeatureEnabled("chat")) && (
        <div className="grid grid-cols-2 gap-3">
          {isFeatureEnabled("complaints") && (
            <button onClick={() => navigate("/complaints?raise=1")} data-testid="quick-complaints"
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-white/20 active:scale-[0.98]">
              <div className="mb-8 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-neon-cyan text-black shadow-glow-cyan"><ChatCircleDots size={20} weight="bold" /></div>
              <p className="font-display text-base font-semibold text-white">Raise a Ticket</p>
              <ArrowRight weight="bold" className="absolute bottom-4 right-4 text-white/40 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
            </button>
          )}
          {isFeatureEnabled("chat") && (
            <button onClick={() => navigate("/chat?dm=1")} data-testid="quick-chat"
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-white/20 active:scale-[0.98]">
              <div className="mb-8 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-neon-lime text-black shadow-glow-lime"><Buildings size={20} weight="bold" /></div>
              <p className="font-display text-base font-semibold text-white">Message Society Manager</p>
              <ArrowRight weight="bold" className="absolute bottom-4 right-4 text-white/40 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
            </button>
          )}
        </div>
      )}

      {/* Daily checklist (read-only for residents) */}
      {isFeatureEnabled("checklist") && (
        <button onClick={() => navigate("/checklist")}
          className="card flex w-full items-center gap-3 p-4 text-left transition-all hover:border-white/20 active:scale-[0.99]">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-neon-lime/15 text-neon-lime"><CheckSquareOffset size={20} weight="duotone" /></div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[15px] font-semibold text-white">Daily Checklist</p>
            <p className="text-[12px] text-white/55">See today's facility operations status</p>
          </div>
          <ArrowRight weight="bold" className="text-white/35" />
        </button>
      )}

      {/* Society staff */}
      {isFeatureEnabled("attendance") && (
        <div>
          <div className="mb-3 flex items-end justify-between">
            <h2 className="font-display text-lg font-bold text-white">{t("home.societyStaff")}</h2>
            <span className="text-[11px] uppercase tracking-[0.22em] text-white/40">{t("home.viewAttendance")}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StaffCard onClick={() => navigate("/staff/SECURITY")} icon={ShieldStar} label={t("staff.security")} accent="cyan" testid="staff-card-security" />
            <StaffCard onClick={() => navigate("/staff/HOUSEKEEPING")} icon={Broom} label={t("staff.housekeeping")} accent="lime" testid="staff-card-housekeeping" />
          </div>
        </div>
      )}

      {/* Announcements: top 3 + See all */}
      {isFeatureEnabled("announcements") && (
        <div>
          <div className="mb-3 flex items-end justify-between">
            <h2 className="font-display text-lg font-bold text-white">{t("home.announcements")}</h2>
            {items.length > 0 && (
              <button data-testid="ann-see-all" onClick={() => setShowAll(true)}
                className="text-[11px] font-bold uppercase tracking-wider text-neon-cyan">{t("common.seeAll")}</button>
            )}
          </div>
          <div className="space-y-2.5">
            {visibleAnn.length === 0 ? (
              <div className="card p-5 text-center text-[13px] text-white/45">No announcements yet.</div>
            ) : visibleAnn.map((a) => (
              <button key={a.id} data-testid={`announcement-${a.id}`} onClick={() => setSelected(a)}
                className="card w-full p-4 text-left hover:border-white/20">
                <div className="mb-1 flex items-center gap-2">
                  <Megaphone size={14} weight="fill" className="text-neon-lime" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-neon-lime">{a.tag}</span>
                </div>
                <p className="font-display text-[15px] font-semibold leading-snug text-white">{a.title}</p>
                <p className="mt-1 line-clamp-1 text-[13px] leading-relaxed text-white/60">{a.body}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent tickets */}
      {isFeatureEnabled("complaints") && (
      <div>
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-lg font-bold text-white">Your Recent Tickets</h2>
          <button onClick={() => navigate("/complaints")} className="text-[11px] font-bold uppercase tracking-wider text-neon-cyan">{t("common.seeAll")}</button>
        </div>
        {complaints.length === 0 ? (
          <button onClick={() => navigate("/complaints?raise=1")} data-testid="home-empty-raise"
            className="card flex w-full items-center gap-3 p-4 text-left transition-all hover:border-white/20">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-neon-cyan text-black shadow-glow-cyan"><Plus size={20} weight="bold" /></div>
            <div>
              <p className="font-display text-[15px] font-semibold text-white">{t("home.noReport")}</p>
              <p className="text-[12px] text-white/55">{t("home.tapToRaise")}</p>
            </div>
            <ArrowRight weight="bold" className="ml-auto text-white/40" />
          </button>
        ) : (
          <div className="space-y-2">
            {complaints.map((c) => (
              <button key={c.id} onClick={() => navigate(`/complaints/${c.id}`)}
                className="card flex w-full items-center gap-3 p-4 text-left transition-all hover:border-white/20 active:scale-[0.99]">
                <div className="flex h-2 w-2 shrink-0 items-center justify-center">
                  <span className={`h-2 w-2 rounded-full ${c.status === "OPEN" ? "bg-neon-pink animate-pulse-soft" : c.status === "IN_PROGRESS" ? "bg-neon-amber" : "bg-neon-lime"}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {c.ticketId && <span className="inline-flex items-center rounded-md border border-neon-cyan/30 bg-neon-cyan/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-neon-cyan">{c.ticketId.replace("TKT-", "#")}</span>}
                    <p className="truncate font-display text-[14px] font-semibold text-white">{c.title}</p>
                  </div>
                  <p className="text-[11px] uppercase tracking-wider text-white/50">{c.status.replace("_", " ")} · {c.category}</p>
                </div>
                <ArrowRight weight="bold" className="ml-auto text-white/35" />
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      <AnimatePresence>
        {selected && <AnnouncementDetail a={selected} onClose={() => setSelected(null)} />}
        {showAll && <AnnouncementsAllSheet items={items} onClose={() => setShowAll(false)} onOpen={(a) => { setShowAll(false); setSelected(a); }} />}
      </AnimatePresence>
    </div>
  );
}

function StaffCard({ onClick, icon: Icon, label, accent, testid }) {
  const bg = accent === "lime" ? "bg-neon-lime" : "bg-neon-cyan";
  const glow = accent === "lime" ? "shadow-glow-lime" : "shadow-glow-cyan";
  return (
    <button onClick={onClick} data-testid={testid}
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-white/20 active:scale-[0.98]">
      <div className={`mb-6 inline-flex h-10 w-10 items-center justify-center rounded-2xl ${bg} text-black ${glow}`}><Icon size={20} weight="bold" /></div>
      <p className="font-display text-[15px] font-semibold text-white">{label}</p>
      <p className="text-[11px] text-white/55">View · attendance</p>
      <ArrowRight weight="bold" className="absolute bottom-4 right-4 text-white/40 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}
